import { useEffect, useState } from 'react';
import { isLocalPath } from '../nav/localPath';
import { useUrlState } from '../nav/urlState';
import type { RentalPreset } from './types';

/**
 * „Wskaż w katalogu” of „Dopożycz” (owner 2026-10-23 21:23): the person goes to the suppliers' catalogs in the picking
 * mode (as „Wybierz z katalogu” of the quote, `catalog/pick.ts`) and a click on a position comes back to the open
 * „Dopożycz” form with that position chosen. Everything lives in the address, so „Wstecz” and a reload keep working:
 * - the catalog: `?wybierz=1&cel=dopozycz&powrot=<the page with the open form>`;
 * - the open form: `dopozycz=<JSON: item, quantity, days and what was typed>` on the page that opened it;
 * - the way back: the same page with `pozycja=<supplierId>:<priceId>` too.
 */

export type RentalPickTarget = { back: string };

/** What the person typed in the form besides the shortage's item, quantity and days (review I2: carried there and back). */
export type CarriedFields = { supplierId?: string; notes?: string; status?: string; returnPersonId?: string; costMode?: 'cennik' | 'reczny'; cost?: string };

/** The open „Dopożycz” form, in the address of the page that opened it (one JSON value). */
export function encodeRentalForm(p: RentalPreset): string {
  const c = p.carried ?? {};
  return JSON.stringify({ i: p.itemId, n: p.itemName, q: p.quantity, f: p.firstDay, l: p.lastDay, ...(Object.keys(c).length > 0 ? { c } : {}) });
}

const str = (v: unknown, max: number): string | undefined => (typeof v === 'string' && v.length <= max ? v : undefined);

export function decodeRentalForm(raw: string | null): RentalPreset | null {
  if (!raw) return null;
  let v: Record<string, unknown>;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    v = parsed as Record<string, unknown>;
  } catch {
    return null;
  }
  const itemId = str(v.i, 64) ?? '';
  const q = v.q;
  if ((itemId !== '' && !/^[\w-]{1,64}$/.test(itemId)) || typeof q !== 'number' || !Number.isInteger(q) || q < 1) return null;
  const day = (d: unknown) => (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : '');
  const preset: RentalPreset = { itemId, itemName: str(v.n, 300) ?? '', quantity: q, firstDay: day(v.f), lastDay: day(v.l) };
  const c = v.c && typeof v.c === 'object' && !Array.isArray(v.c) ? (v.c as Record<string, unknown>) : null;
  if (c) {
    const carried: CarriedFields = {};
    const id = (x: unknown) => (typeof x === 'string' && /^[\w-]{0,64}$/.test(x) ? x : undefined);
    if (id(c.supplierId) !== undefined) carried.supplierId = id(c.supplierId)!;
    if (str(c.notes, 2000) !== undefined) carried.notes = str(c.notes, 2000)!;
    if (typeof c.status === 'string' && ['planowane', 'potwierdzone', 'odebrane', 'zwrocone'].includes(c.status)) carried.status = c.status;
    if (id(c.returnPersonId) !== undefined) carried.returnPersonId = id(c.returnPersonId)!;
    if (c.costMode === 'cennik' || c.costMode === 'reczny') carried.costMode = c.costMode;
    if (str(c.cost, 30) !== undefined) carried.cost = str(c.cost, 30)!;
    if (Object.keys(carried).length > 0) preset.carried = carried;
  }
  return preset;
}

/** A local address of the app (review I1: one rule for every way back, `nav/localPath`). */
export { isLocalPath };

/** `path?query` with these parameters set (null: removed). */
function withParams(url: string, set: Record<string, string | null>): string {
  const [path, query = ''] = url.split('?');
  const q = new URLSearchParams(query);
  for (const [k, v] of Object.entries(set)) {
    if (v === null) q.delete(k);
    else q.set(k, v);
  }
  const s = q.toString();
  return s ? `${path}?${s}` : path!;
}

/** The page `here` (path + query) with the form `preset` open on it again (the way back from the suppliers' catalogs). */
export const rentalFormUrl = (here: string, preset: RentalPreset): string => withParams(here, { dopozycz: encodeRentalForm(preset), pozycja: null });

/** The suppliers' catalogs in the picking mode for the form `preset` open on the page `here` (path + query). */
export function rentalPickUrl(here: string, preset: RentalPreset): string {
  const back = rentalFormUrl(here, preset);
  return `/katalog/dostawcy?${new URLSearchParams({ wybierz: '1', cel: 'dopozycz', powrot: back }).toString()}`;
}

/** The picking mode for „Dopożycz” of a catalog page, or null. */
export function rentalPickTarget(params: URLSearchParams): RentalPickTarget | null {
  const back = params.get('powrot');
  if (params.get('wybierz') !== '1' || params.get('cel') !== 'dopozycz' || !back || !isLocalPath(back)) return null;
  return { back };
}

/** Keeps the picking mode between the suppliers' pages. */
export const rentalPickSearch = (t: RentalPickTarget): string => `?${new URLSearchParams({ wybierz: '1', cel: 'dopozycz', powrot: t.back }).toString()}`;

/** Back to the open form with the position chosen. */
export const rentalReturnUrl = (t: RentalPickTarget, supplierId: string, priceId: string): string => withParams(t.back, { pozycja: `${supplierId}:${priceId}` });

/** The position chosen in the catalog, from the address of the page with the form. */
export function parsePicked(raw: string | null): { supplierId: string; priceId: string } | null {
  const m = raw ? /^([\w-]{1,64}):([\w-]{1,64})$/.exec(raw) : null;
  return m ? { supplierId: m[1]!, priceId: m[2]! } : null;
}

/**
 * For the pages that open „Dopożycz”: the form to open again after „Wskaż w katalogu”, with the chosen position.
 * Review: taken out of the address once read (kept here), so another tab or page does not open it again.
 */
export function useRentalReturn(): { preset: RentalPreset; picked: { supplierId: string; priceId: string } | null; close: () => void } | null {
  const { params, update } = useUrlState();
  const raw = params.get('dopozycz');
  const [kept, setKept] = useState<{ preset: RentalPreset; picked: { supplierId: string; priceId: string } | null } | null>(null);
  const found = raw ? decodeRentalForm(raw) : null;
  const pickedRaw = params.get('pozycja');
  useEffect(() => {
    if (!raw) return;
    if (found) setKept({ preset: found, picked: parsePicked(pickedRaw) });
    update({ dopozycz: null, pozycja: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raw, pickedRaw]);
  const now = kept ?? (found ? { preset: found, picked: parsePicked(pickedRaw) } : null);
  if (!now) return null;
  return { ...now, close: () => setKept(null) };
}
